import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

export type RefreshHandler = () => void | Promise<unknown>

interface RefreshContextValue {
  /** Runs the registered handler, or resolves immediately if there is none. */
  runRefresh: () => Promise<void>
  /** True when some surface has claimed the pull. */
  hasHandler: () => boolean
  register: (handler: RefreshHandler) => () => void
}

const RefreshContext = createContext<RefreshContextValue | undefined>(undefined)

/**
 * Who answers a pull-to-refresh.
 *
 * A registry rather than a prop threaded through the layout, because the component
 * that draws the gesture (one, in MainLayout) and the component that knows how to
 * refetch (whichever page is mounted) are nowhere near each other in the tree, and
 * the answer changes on every navigation.
 *
 * Deliberately single-slot: the pull is one gesture on one screen, so the last
 * surface to register owns it. Registration returns its own unregister, and that
 * unregister only clears the slot if it is still the current occupant — otherwise a
 * page unmounting *after* its replacement mounted would wipe the new handler and
 * leave the screen silently falling back to a reload.
 */
export const RefreshProvider = ({ children }: { children: ReactNode }) => {
  const handlerRef = useRef<RefreshHandler | null>(null)

  const register = useCallback((handler: RefreshHandler) => {
    handlerRef.current = handler
    return () => {
      if (handlerRef.current === handler) {
        handlerRef.current = null
      }
    }
  }, [])

  const hasHandler = useCallback(() => handlerRef.current !== null, [])

  const runRefresh = useCallback(async () => {
    const handler = handlerRef.current
    if (!handler) {
      return
    }
    await handler()
  }, [])

  const value = useMemo(
    () => ({ runRefresh, hasHandler, register }),
    [runRefresh, hasHandler, register],
  )

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
}

export const useRefresh = (): RefreshContextValue => {
  const context = useContext(RefreshContext)
  if (!context) {
    throw new Error('useRefresh must be used within RefreshProvider')
  }
  return context
}

/**
 * Claim the pull for this surface.
 *
 * Pass a memoised callback — an inline arrow re-registers on every render, which is
 * harmless for the slot but makes the effect churn. The handler should resolve when
 * the data has landed, because that is what holds the spinner up; a fire-and-forget
 * dispatch will flash and vanish.
 *
 * A page that registers nothing still gets a working pull: PullToRefresh reloads the
 * document instead. That fallback is what makes the gesture safe to add everywhere at
 * once rather than page by page.
 *
 * Pass `null` to claim nothing. That is the only way for a surface that is sometimes the
 * page and sometimes an overlay — AIM, which also renders as a floating bubble over
 * whatever page you were on — to register conditionally: the slot is single and global, so
 * an unconditional claim from inside the bubble would evict the host page's own handler and
 * answer its pull with the wrong refetch.
 */
export const useRefreshHandler = (handler: RefreshHandler | null): void => {
  const { register } = useRefresh()
  useEffect(() => {
    if (!handler) {
      return
    }
    return register(handler)
  }, [register, handler])
}
